// Volume histogram — 24h hourly buckets reflecting the current
// slice. Compact bar row sized to fit between the toolbar and the
// table. Click a bar → narrow dateScope to a tighter window if the
// bar shows volume. Hidden on non-events tabs (parent guards with
// baseView === 'events' before rendering).
//
// Extracted from DataSurfaceV3 during the agent-velocity refactor.

import * as React from 'react';

export function VolumeHistogram({
  rows,
  onClickBar,
}: {
  rows: Array<{ ts: number; count: number }> | null | undefined;
  onClickBar: () => void;
}): React.ReactElement | null {
  if (!rows || rows.length === 0) return null;
  const now = Date.now();
  // Build a 24-bucket array from oldest→newest, filling in zeros
  // for hours with no data so the bar row reads as a calendar not a
  // sparse list.
  const HOUR_MS = 60 * 60 * 1000;
  const buckets: Array<{ ts: number; count: number }> = [];
  const alignedStart = Math.floor(now / HOUR_MS) * HOUR_MS - 23 * HOUR_MS;
  const dataMap = new Map(rows.map(r => [r.ts, r.count]));
  for (let i = 0; i < 24; i++) {
    const ts = alignedStart + i * HOUR_MS;
    buckets.push({ ts, count: dataMap.get(ts) ?? 0 });
  }
  const max = Math.max(1, ...buckets.map(b => b.count));
  const total = buckets.reduce((s, b) => s + b.count, 0);
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', gap: 2,
      padding: '4px 10px',
      background: 'var(--bg-sunken)',
      border: '1px solid var(--line)', borderRadius: 6,
      height: 44,
    }}>
      <div style={{
        fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--ink-3)',
        minWidth: 100, textTransform: 'uppercase', letterSpacing: '0.08em',
        alignSelf: 'center',
      }}>24h volume · {total.toLocaleString()}</div>
      <div style={{ display: 'flex', flex: 1, alignItems: 'flex-end', gap: 1, height: 32 }}>
        {buckets.map((b, i) => {
          const ratio = b.count / max;
          const hour = new Date(b.ts).toLocaleTimeString([], { hour: '2-digit' });
          return (
            <button
              key={i}
              type="button"
              onClick={onClickBar}
              title={`${hour} · ${b.count.toLocaleString()} event${b.count === 1 ? '' : 's'}\nClick to narrow to last hour`}
              aria-label={`${hour}: ${b.count} event${b.count === 1 ? '' : 's'} — click to narrow to last hour`}
              style={{
                flex: 1, minWidth: 8, height: '100%',
                display: 'flex', alignItems: 'flex-end',
                background: 'transparent', border: 'none',
                padding: 0, cursor: 'pointer',
              }}
            >
              <div style={{
                width: '100%',
                height: `${Math.max(2, ratio * 100)}%`,
                background: b.count > 0 ? 'oklch(0.62 0.18 145 / 0.7)' : 'oklch(0.92 0.01 250)',
                borderRadius: 1,
                transition: 'height 0.3s ease',
              }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
